import { useEffect, useState, useSyncExternalStore, type FC } from "react";

import { createPortal } from "react-dom";

import { Loader } from "@eisox/design-system";
import { useIsFetching } from "@tanstack/react-query";

import { m } from "../../paraglide/messages";
import { isOverlayQuery } from "../loading-overlay.helper";
import { createLoadingOverlayStore } from "../loading-overlay.store";
import type { LoadingOverlayProps } from "../loading-overlay.type";

const LoadingOverlay: FC<LoadingOverlayProps> = ({ busy = false }) => {
  const [store] = useState(createLoadingOverlayStore);
  const fetching = useIsFetching({ predicate: isOverlayQuery }) > 0;
  const visible = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);

  useEffect(() => {
    store.setBusy(fetching || busy);
    return () => store.setBusy(false);
  }, [busy, fetching, store]);

  if (!visible) return null;

  // The backdrop centres the panel, so the panel carries no positioning of its own — which is
  // what used to make `contentShow` and Tailwind's `translate` utilities fight over `transform`.
  return createPortal(
    <div className="animate-overlayShow overlay">
      <div
        role="status"
        className="animate-contentShow gap-10 flex h-[226px] max-h-[calc(100dvh-40px)] w-[518px] max-w-[calc(100dvw-40px)] flex-col items-center justify-center rounded-[12px] bg-primary-white shadow-[var(--shadow-elevated)]"
      >
        <p className="font-semibold text-2xl text-primary-dark-blue">{m.loading()}</p>
        <Loader />
      </div>
    </div>,
    document.body,
  );
};

LoadingOverlay.displayName = "LoadingOverlay";

export { LoadingOverlay };
